var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useState, useEffect, useMemo } from "react";
import { MultiColumnList, Select, Button, } from "@folio/stripes/components";
import { SearchAndSortNoResultsMessage as NoResultsMessage, } from "@folio/stripes/smart-components";
import { useIntl } from "react-intl";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter, } from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy, } from "@dnd-kit/sortable";
import { useTableColumns } from "../hooks/useTableColumns";
import { useColumnLayout } from "../hooks/useColumnLayout";
import { ColumnHeader } from "./ColumnHeader";
import { pageSizeDataOptions } from "../utils/constants";
import css from "./TableComponent.module.css";
import { isPagingCapped } from "../utils/pagingCap";
function TableComponent(_a) {
    var _this = this;
    var data = _a.data, columns = _a.columns, _b = _a.pageSize, pageSize = _b === void 0 ? 20 : _b, _c = _a.totalCount, totalCount = _c === void 0 ? 0 : _c, pagingOffset = _a.pagingOffset, onNeedMoreData = _a.onNeedMoreData, onPageSizeChange = _a.onPageSizeChange, searchTerm = _a.searchTerm, filterPaneIsVisible = _a.filterPaneIsVisible, toggleFilterPane = _a.toggleFilterPane, source = _a.source, sortedColumn = _a.sortedColumn, sortDirection = _a.sortDirection, onHeaderClick = _a.onHeaderClick, layoutKey = _a.layoutKey, maxOffset = _a.maxOffset;
    var _d = useState(0), offset = _d[0], setOffset = _d[1];
    var formatMessage = useIntl().formatMessage;
    var layout = useColumnLayout(layoutKey !== null && layoutKey !== void 0 ? layoutKey : "", columns);
    var layoutEnabled = !!layoutKey;
    var effectiveColumns = layoutEnabled ? layout.columns : columns;
    var _f = useTableColumns(effectiveColumns), columnMapping = _f.columnMapping, visibleColumns = _f.visibleColumns, formatter = _f.formatter, columnWidths = _f.columnWidths, sortableFields = _f.sortableFields, nonInteractiveHeaders = _f.nonInteractiveHeaders;
    var sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
    var orderedIds = useMemo(function () { return effectiveColumns.map(function (col) { var _a; return (_a = col.id) !== null && _a !== void 0 ? _a : col.key; }); }, [effectiveColumns]);
    // Wrap each header label in an interactive ColumnHeader (drag to reorder, drag edge to resize).
    var interactiveColumnMapping = useMemo(function () {
        if (!layoutEnabled)
            return columnMapping;
        return effectiveColumns.reduce(function (acc, col) {
            var _a;
            var id = (_a = col.id) !== null && _a !== void 0 ? _a : col.key;
            acc[id] = (React.createElement(ColumnHeader, { id: id, label: col.name, onResize: layout.setWidth }));
            return acc;
        }, {});
    }, [layoutEnabled, columnMapping, effectiveColumns, layout.setWidth]);
    var handleDragEnd = function (event) {
        var active = event.active, over = event.over;
        if (!over || active.id === over.id)
            return;
        layout.reorder(String(active.id), String(over.id));
    };
    useEffect(function () {
        if (pagingOffset !== undefined) {
            setOffset(pagingOffset);
        }
    }, [pagingOffset]);
    var handleNeedMoreData = function (_amount, index) { return __awaiter(_this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            if (onNeedMoreData)
                onNeedMoreData(index);
            setOffset(index);
            return [2 /*return*/];
        });
    }); };
    var handlePageSizeChange = function (event) {
        setOffset(0);
        onPageSizeChange === null || onPageSizeChange === void 0 ? void 0 : onPageSizeChange(Number(event.target.value));
    };
    var resultsStatusMessage = source ? (React.createElement("div", { "data-test-user-search-no-results-message": true },
        React.createElement(NoResultsMessage, { source: source, searchTerm: searchTerm !== null && searchTerm !== void 0 ? searchTerm : "", filterPaneIsVisible: filterPaneIsVisible, toggleFilterPane: toggleFilterPane }))) : ("no source yet");
    var pagingCapped = isPagingCapped(offset, pageSize, totalCount, maxOffset);
    var table = (React.createElement(MultiColumnList, __assign({ contentData: data }, (columnWidths && {
        columnWidths: columnWidths,
    }), { columnMapping: interactiveColumnMapping, formatter: formatter, visibleColumns: visibleColumns, sortedColumn: sortedColumn, sortDirection: sortDirection, nonInteractiveHeaders: onHeaderClick
            ? nonInteractiveHeaders
            : undefined, onHeaderClick: onHeaderClick
            ? function (_e, meta) { return onHeaderClick(String(meta.name)); }
            : undefined, sortableFields: onHeaderClick ? sortableFields : undefined,
        showSortIndicator: !!onHeaderClick,
        // stop the pager at the backend's offset limit (undefined = MCL decides)
        pagingCanGoNext: pagingCapped ? false : undefined, pagingType: "prev-next", isEmptyMessage: resultsStatusMessage, pagingOffset: offset, pageAmount: pageSize, totalCount: totalCount, onNeedMoreData: function (pageAmount, newOffset) {
            return handleNeedMoreData(pageAmount, newOffset);
        } })));
    return (React.createElement("div", { className: css.tableWrapper },
        layoutEnabled && layout.isCustomized && (React.createElement("div", { className: css.layoutActions },
            React.createElement(Button, { buttonStyle: "none", marginBottom0: true, onClick: layout.reset, "aria-label": formatMessage({
                    id: "ui-br-agency.table.columns.reset",
                }) }, formatMessage({ id: "ui-br-agency.table.columns.reset" })))),
        layoutEnabled ? (React.createElement(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd },
            React.createElement(SortableContext, { items: orderedIds, strategy: horizontalListSortingStrategy }, table))) : (table),
        pagingCapped && (React.createElement("div", { className: css.pagingCapHint, role: "status" }, formatMessage({ id: "ui-br-agency.table.paging.capped" }, { max: (maxOffset !== null && maxOffset !== void 0 ? maxOffset : 0).toLocaleString("cs-CZ") }))),
        onPageSizeChange && data.length > 0 && (React.createElement("div", { className: css.pageSizeRow },
            React.createElement("span", { className: css.pageSizeLabel },
                formatMessage({ id: "ui-br-agency.records-per-page" }),
                ":"),
            React.createElement("div", { className: css.pageSizeSelect },
                React.createElement(Select, { value: String(pageSize), dataOptions: pageSizeDataOptions, onChange: handlePageSizeChange, "aria-label": formatMessage({
                        id: "ui-br-agency.records-per-page",
                    }), marginBottom0: true }))))));
}
export default TableComponent;
